import React, { useState, useEffect, useMemo } from 'react';
import styled, { css } from 'styled-components';
import type { QueryObserverResult } from '@tanstack/react-query';
import cloneDeep from 'lodash/cloneDeep';
import isEqual from 'lodash/isEqual';
import set from 'lodash/set';

import { InputOptionalInfo, Select } from 'components/common';
import { Alert, BootstrapModalForm, Button, Input } from 'components/bootstrap';
import useSendTelemetry from 'logic/telemetry/useSendTelemetry';
import useLocation from 'routing/useLocation';
import { TELEMETRY_EVENT_TYPE } from 'telemetry/Constants';
import { getPathnameWithoutId } from 'util/URLUtils';

import type { AthenaConnectorCredentials, SaveAthenaConnectorCredentials } from './useDataLakeConnectors';
import { saveDataLakeConnector } from './useDataLakeConnectors';
import useDataLakeConnectorById from './useDataLakeConnectorById';
import useDataLakeConnectorRegions from './useDataLakeConnectorRegions';

const SubHeadline = styled.h4(
  ({ theme }) => css`
    margin-top: ${theme.spacings.md};
    margin-bottom: ${theme.spacings.xs};
  `,
);

type Props = {
  connectorId?: string;
  onCancel: () => void;
  onRefetch: () => Promise<QueryObserverResult<AthenaConnectorCredentials[], Error>>;
};

type AthenaConnector = AthenaConnectorCredentials & SaveAthenaConnectorCredentials;

const EditConnectorModal = ({ connectorId = undefined, onRefetch, onCancel }: Props) => {
  const initialValues = useMemo(
    () => ({
      id: connectorId,
      aws_region: '',
      aws_access_key: '',
      aws_iam_role: '',
      stream_id: '',
      stream_title: connectorId ? undefined : '',
      stream_description: connectorId ? undefined : '',
      data_lake_name: '',
      s3_output_bucket: '',
      aws_secret_key: {
        set_value: '',
        is_set: false,
      },
    }),
    [connectorId],
  );

  const [connector, setConnector] = useState<AthenaConnector>(initialValues);

  const { data } = useDataLakeConnectorById(connectorId);
  const { data: regions } = useDataLakeConnectorRegions();
  const sendTelemetry = useSendTelemetry();
  const { pathname } = useLocation();

  const regionOptions = regions?.map((region) => ({ label: region, value: region }));

  const savedConnector = useMemo(
    () =>
      connectorId
        ? {
            ...data,
            aws_secret_key: {
              is_set: connector?.aws_secret_key?.is_set,
              set_value: '',
            },
            stream_title: undefined,
            stream_description: undefined,
          }
        : initialValues,
    [data, connector?.aws_secret_key?.is_set, connectorId, initialValues],
  );

  const isConnectorFormDirty = !isEqual(connector, savedConnector);

  useEffect(() => {
    if (data) {
      setConnector({
        ...initialValues,
        ...data,
        aws_secret_key: {
          ...initialValues.aws_secret_key,
          ...data.aws_secret_key,
        },
      });
    }
  }, [data, initialValues]);

  const updateConnectorField = (event) => {
    const newConnector = cloneDeep(connector);
    set(newConnector, event.target.name, event.target.value);
    setConnector(newConnector);
  };

  const updateConnectorFieldByName = (name: string, value: string) => {
    const newConnector = cloneDeep(connector);
    set(newConnector, name, value);
    setConnector(newConnector);
  };

  const toggleResetAwsSecretKey = () => {
    setConnector({
      ...connector,
      aws_secret_key: {
        ...connector.aws_secret_key,
        is_set: !connector.aws_secret_key.is_set,
      },
    });
  };

  const onSubmitForm = async () => {
    sendTelemetry(
      connectorId
        ? TELEMETRY_EVENT_TYPE.DATALAKE.CONNECTORS.EDIT_CONNECTOR_MODAL_SAVE_CLICKED
        : TELEMETRY_EVENT_TYPE.DATALAKE.CONNECTORS.ADD_CONNECTOR_MODAL_SAVE_CLICKED,
      {
        app_pathname: getPathnameWithoutId(pathname),
      },
    );

    await saveDataLakeConnector(connector);
    await onRefetch();
    onCancel();
  };

  return (
    <BootstrapModalForm
      show
      title={connectorId ? 'Edit Connector' : 'Add Connector'}
      onSubmitForm={onSubmitForm}
      onCancel={onCancel}
      submitButtonDisabled={!isConnectorFormDirty}
      submitButtonText="Save">
      <Alert bsStyle="info">
        Each connector must be associated with a system managed stream to which data retrieved from this connector will
        be stored. It is not possible to add Stream Rules, Pipeline Rules, Routing Destinations or Filter Rules to a
        Stream associated with a Connector.
      </Alert>
      <fieldset>
        <Input
          id="data_lake_name"
          type="text"
          label="Data Lake Name"
          name="data_lake_name"
          value={connector?.data_lake_name}
          onChange={updateConnectorField}
          required
        />
        <Input
          id="s3_output_bucket"
          type="text"
          label="S3 Output Bucket"
          name="s3_output_bucket"
          placeholder="s3://"
          value={connector?.s3_output_bucket}
          onChange={updateConnectorField}
          required
        />
        <Input label="AWS Region" help="The AWS Region your service is running in.">
          <Select
            id="aws_region"
            name="aws_region"
            placeholder="Select AWS Region"
            options={regionOptions || []}
            onChange={(option) => {
              updateConnectorFieldByName('aws_region', option);
            }}
            value={connector?.aws_region || ''}
            required
          />
        </Input>
        <Input
          id="aws_access_key"
          type="text"
          label="AWS Access Key"
          placeholder="AK****************"
          help='Your AWS Key should be a 20-character long, alphanumeric string that starts with the letters "AK".'
          name="aws_access_key"
          value={connector?.aws_access_key}
          onChange={updateConnectorField}
          required
        />
        <Input
          id="aws_secret_key"
          type="password"
          label="AWS Secret Key"
          help="Your AWS Secret is usually a 40-character long, base-64 encoded string."
          name="aws_secret_key.set_value"
          placeholder={connector?.aws_secret_key?.is_set ? '****************' : undefined}
          value={connector?.aws_secret_key?.is_set ? '' : connector?.aws_secret_key?.set_value}
          onChange={updateConnectorField}
          disabled={connector?.aws_secret_key?.is_set}
          buttonAfter={
            connectorId ? (
              <Button type="button" onClick={toggleResetAwsSecretKey}>
                {connector?.aws_secret_key?.is_set ? 'Reset' : 'Undo Reset'}
              </Button>
            ) : undefined
          }
          required
        />
        <Input
          id="aws_iam_role"
          type="text"
          label="AWS IAM Role"
          help="AWS IAM Role to be assumed for the connector (optional)"
          name="aws_iam_role"
          placeholder="arn:aws:iam::****************"
          value={connector?.aws_iam_role}
          onChange={updateConnectorField}
        />
        {!connectorId && (
          <>
            <SubHeadline>Create New Stream</SubHeadline>
            <Alert bsStyle="info">
              <p>This stream will be permanently assigned to this connector.</p>
              <p>
                Users without the administrator role must to be granted permission to view this stream in order to
                select this connector on the Preview page.
              </p>
            </Alert>
            <Input
              label="Stream Title"
              name="stream_title"
              id="stream_title"
              type="text"
              help="A descriptive name of the new stream"
              value={connector?.stream_title}
              onChange={updateConnectorField}
              required
            />
            <Input
              label={
                <>
                  Stream Description <InputOptionalInfo />
                </>
              }
              name="stream_description"
              id="stream_description"
              type="text"
              help="What kind of messages are routed into this stream?"
              value={connector?.stream_description}
              onChange={updateConnectorField}
            />
          </>
        )}
      </fieldset>
    </BootstrapModalForm>
  );
};

export default EditConnectorModal;
